/**
 * PATH: packages/bills/src/components/pdfFieldRegions.ts
 * FILE_ID: BILLS-PKG-CMP-PDFFIELDREGIONS-001
 * PURPOSE: Derive per-field highlight regions CLIENT-SIDE by locating each
 *          extracted value (scalars + every line-item cell) inside the PDF's
 *          own text runs. The backend extractor emits no coordinates yet
 *          (``field_regions`` is always empty), so without this the sidebar→
 *          PDF cross-highlight had nothing to draw.
 *
 * FLOW:
 *   TextBox[][] (per page, from extractTextBoxes)
 *     ─▶ rows (reading order) ─▶ normalised token stream per page
 *   BillRegionValues (vendor, totals, line cells)
 *     ─▶ token-subsequence / numeric / date matching
 *     ─▶ BillFieldRegion[] keyed with the SAME vocabulary as billFieldKeys
 *        (``vendor_name``, ``line.0.description``, …) so the existing
 *        RegionBox overlay + activeField plumbing light up unchanged.
 *
 * WHY best-effort: scanned pages have no text runs, and extractor values can
 * differ from the printed text (reformatting, OCR). A miss just means no box
 * for that field — never a wrong-looking full-page rectangle.
 *
 * RELATED:
 *   - PdfCanvasViewer.tsx: merges these with backend field_regions (backend
 *     wins per field) and renders RegionBox.
 *   - pdfTextGeometry.ts: source of the TextBox shape (normalised 0..1).
 */

import type { BillFieldRegion } from '../types';
import { billFieldValue, lineFieldKey } from './billFieldKeys';
import type { TextBox } from './pdfTextGeometry';

/** The extracted values we try to anchor onto the document. Structurally a
 *  subset of ``BillDetail`` so callers can pass the bill straight in. */
export interface BillRegionValues {
  vendor_name?: string | null;
  vendor_email?: string | null;
  vendor_abn?: string | null;
  invoice_number?: string | null;
  reference?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  subtotal?: string | null;
  gst?: string | null;
  total?: string | null;
  line_items?: Array<{
    description?: string;
    quantity?: number | null;
    unit_amount?: number | null;
    line_total?: number | null;
  }>;
}

/* ------------------------------------------------------------------ */
/* Tokenisation                                                        */
/* ------------------------------------------------------------------ */

/**
 * Canonical form of one whitespace-separated word so extractor values and
 * PDF text compare equal despite formatting: "1,057.50" ≡ "1057.5" ≡ 1057.5,
 * "@SPARK" ≡ "spark", "INV-3099" ≡ "inv3099". Empty string = drop the token.
 */
export function normToken(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  // Numeric? Strip currency/grouping first so "$1,057.50" parses.
  const numeric = t.replace(/[,$\s]/g, '');
  if (/^-?\d+(\.\d+)?$/.test(numeric)) {
    const n = Number(numeric);
    if (Number.isFinite(n)) return String(n);
  }
  return t.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function tokenize(value: string): string[] {
  return value
    .split(/\s+/)
    .map(normToken)
    .filter(Boolean);
}

/** Canonical numeric string for an amount value ("2,057.50" → "2057.5"). */
function normAmount(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,$\s]/g, ''));
  return Number.isFinite(n) ? String(n) : null;
}

/* ------------------------------------------------------------------ */
/* Page index: rows in reading order + token stream                    */
/* ------------------------------------------------------------------ */

interface Tok {
  norm: string;
  /** Index into the page's TextBox array. */
  run: number;
  /** Row (visual line) the run sits on. */
  row: number;
}

interface PageIndex {
  boxes: TextBox[];
  tokens: Tok[];
  rowCount: number;
}

/** Runs whose vertical bands overlap belong to the same visual row. */
function buildPageIndex(boxes: TextBox[]): PageIndex {
  const order = boxes
    .map((_, i) => i)
    .sort((a, b) => boxes[a].y0 - boxes[b].y0 || boxes[a].x0 - boxes[b].x0);
  const rowOf = new Array<number>(boxes.length).fill(0);
  let row = -1;
  let bandY1 = -Infinity;
  for (const i of order) {
    const b = boxes[i];
    const yc = (b.y0 + b.y1) / 2;
    if (yc > bandY1) {
      row += 1;
      bandY1 = b.y1;
    } else {
      bandY1 = Math.max(bandY1, b.y1);
    }
    rowOf[i] = row;
  }
  // Reading order: row, then x.
  const reading = boxes
    .map((_, i) => i)
    .sort((a, b) => rowOf[a] - rowOf[b] || boxes[a].x0 - boxes[b].x0);
  const tokens: Tok[] = [];
  for (const i of reading) {
    for (const norm of tokenize(boxes[i].str)) {
      tokens.push({ norm, run: i, row: rowOf[i] });
    }
  }
  return { boxes, tokens, rowCount: row + 1 };
}

/* ------------------------------------------------------------------ */
/* Matching primitives                                                 */
/* ------------------------------------------------------------------ */

interface Match {
  /** Token indices (into PageIndex.tokens) that were consumed. */
  consumed: number[];
  matched: number;
}

/**
 * Find the target token sequence in a page's token stream, tolerating skipped
 * document tokens between matches. Skips are what make wrapped line-item
 * descriptions work: reading order interleaves the qty/price cells between the
 * description's wrapped rows, so a strictly contiguous match would fail.
 */
function matchTokens(page: PageIndex, target: string[]): Match | null {
  if (target.length === 0) return null;
  const capped = target.slice(0, 60);
  // Small targets must match fully; longer ones tolerate extractor drift.
  const minMatched =
    capped.length <= 2 ? capped.length : Math.max(2, Math.ceil(capped.length * 0.6));
  const maxSkips = capped.length * 2 + 8;
  let best: Match | null = null;
  for (let start = 0; start < page.tokens.length; start++) {
    if (page.tokens[start].norm !== capped[0]) continue;
    let ti = 0;
    let skips = 0;
    const consumed: number[] = [];
    for (let i = start; i < page.tokens.length && ti < capped.length; i++) {
      if (page.tokens[i].norm === capped[ti]) {
        consumed.push(i);
        ti += 1;
      } else {
        skips += 1;
        if (skips > maxSkips) break;
      }
    }
    if (ti >= minMatched && (!best || ti > best.matched)) {
      best = { consumed, matched: ti };
      if (ti === capped.length) break; // earliest full match wins
    }
  }
  return best;
}

/** Union the consumed runs into one bbox PER ROW (tight multi-line boxes). */
function regionsFromConsumed(
  page: PageIndex,
  pageIdx: number,
  field: string,
  consumed: number[],
): BillFieldRegion[] {
  const byRow = new Map<number, { x0: number; y0: number; x1: number; y1: number }>();
  const seenRuns = new Set<number>();
  for (const ti of consumed) {
    const tok = page.tokens[ti];
    if (seenRuns.has(tok.run)) continue;
    seenRuns.add(tok.run);
    const b = page.boxes[tok.run];
    const cur = byRow.get(tok.row);
    if (!cur) byRow.set(tok.row, { x0: b.x0, y0: b.y0, x1: b.x1, y1: b.y1 });
    else {
      cur.x0 = Math.min(cur.x0, b.x0);
      cur.y0 = Math.min(cur.y0, b.y0);
      cur.x1 = Math.max(cur.x1, b.x1);
      cur.y1 = Math.max(cur.y1, b.y1);
    }
  }
  return [...byRow.values()].map((r) => ({
    field,
    page: pageIdx,
    bbox: [r.x0, r.y0, r.x1, r.y1] as [number, number, number, number],
  }));
}

/** All token indices whose norm equals `norm` (numeric candidates). */
function tokensEqual(page: PageIndex, norm: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < page.tokens.length; i++) {
    if (page.tokens[i].norm === norm) out.push(i);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

/** Token-sequence candidates for an ISO date ("2025-02-20" → "20 feb 2025",
 *  "20/02/2025", …). Slash/dot dates collapse to one alnum token. */
function dateCandidates(iso: string): string[][] {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return [tokenize(iso)];
  const [, y, mm, dd] = m;
  const d = String(Number(dd));
  const mo = String(Number(mm));
  const monthFull = MONTHS[Number(mm) - 1] ?? '';
  const monthAbbr = monthFull.slice(0, 3);
  const y2 = y.slice(2);
  const out: string[][] = [
    [d, monthAbbr, y],
    [d, monthFull, y],
    [monthAbbr, d, y],
    [monthFull, d, y],
    [`${d}${monthAbbr}${y}`],
    [`${dd}${mm}${y}`], // 20/02/2025 → "20022025"
    [`${d}${mo}${y}`], // 20/2/2025
    [`${dd}${mm}${y2}`], // 20/02/25
    [`${y}${mm}${dd}`], // 2025-02-20
  ];
  return out;
}

/* ------------------------------------------------------------------ */
/* Amounts with label context (subtotal / gst / total rows)            */
/* ------------------------------------------------------------------ */

function rowsWithToken(page: PageIndex, word: string): Set<number> {
  const rows = new Set<number>();
  for (const t of page.tokens) if (t.norm === word) rows.add(t.row);
  return rows;
}

/**
 * Pick the token for a totals amount, preferring the row that carries the
 * matching label ("subtotal" / "gst" / "total") and, for "total", rejecting
 * the "TOTAL GST" row. Falls back to the LAST occurrence on the page
 * (totals live at the bottom).
 */
function pickAmountToken(
  page: PageIndex,
  norm: string,
  label: 'subtotal' | 'gst' | 'total' | null,
): number | null {
  const candidates = tokensEqual(page, norm);
  if (candidates.length === 0) return null;
  if (label) {
    const labelRows = rowsWithToken(page, label);
    const gstRows = label === 'total' ? rowsWithToken(page, 'gst') : new Set<number>();
    const subRows = label === 'total' ? rowsWithToken(page, 'subtotal') : new Set<number>();
    for (const ti of candidates) {
      const row = page.tokens[ti].row;
      if (labelRows.has(row) && !gstRows.has(row) && !subRows.has(row)) return ti;
    }
  }
  return candidates[candidates.length - 1];
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

/** Forgiveness margin for hit-testing a click against a region box. */
const HIT_TOLERANCE = 0.004;

/**
 * f(one page's regions, click point) → the field whose box contains the
 * point, smallest box winning overlaps (a line-total cell inside a line
 * band should beat the description). Lets a bare click on the PDF route
 * straight to the field it belongs to — no arming needed.
 */
export function fieldRegionAtPoint(
  regions: BillFieldRegion[],
  x: number,
  y: number,
): string | null {
  let best: string | null = null;
  let bestArea = Infinity;
  for (const r of regions) {
    const [x0, y0, x1, y1] = r.bbox;
    if (
      x >= x0 - HIT_TOLERANCE &&
      x <= x1 + HIT_TOLERANCE &&
      y >= y0 - HIT_TOLERANCE &&
      y <= y1 + HIT_TOLERANCE
    ) {
      const area = (x1 - x0) * (y1 - y0);
      if (area < bestArea) {
        bestArea = area;
        best = r.field;
      }
    }
  }
  return best;
}

/**
 * Are two field values the SAME value, ignoring formatting noise? The edit
 * panel round-trips every line-item cell through ``Number()`` when ANY cell
 * of the row is touched, so the live draft carries "400" where the document
 * (and the base) say "400.00". Treating that as an edit patched every
 * numeric cell after the first keystroke — and once patched fields stopped
 * drawing their row highlight boxes, ALL the line-item boxes vanished
 * ("bounded box not working at all", 2026-07-25). Numbers compare
 * numerically (comma separators stripped); text compares with collapsed
 * whitespace.
 */
export function sameFieldValue(a: string, b: string): boolean {
  if (a === b) return true;
  if (a !== '' && b !== '') {
    const na = Number(a.replace(/,/g, ''));
    const nb = Number(b.replace(/,/g, ''));
    if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  }
  return a.replace(/\s+/g, ' ') === b.replace(/\s+/g, ' ');
}

/**
 * f(as-extracted values, live draft values, field keys) → the fields whose
 * live value DIFFERS from what the document prints, mapped to that live
 * value. Drives the "amended value" patches painted over the PDF: the page
 * itself is a raster of the original invoice, so an edit is shown by
 * covering the printed text with the corrected value at the same spot
 * (2026-07-24 "its not getting updated in the pdf").
 */
export function editedFieldValues(
  base: unknown,
  live: unknown,
  fields: Iterable<string>,
): Map<string, string> {
  const out = new Map<string, string>();
  if (!base || !live || base === live) return out;
  for (const field of fields) {
    const liveValue = billFieldValue(live, field).trim();
    const baseValue = billFieldValue(base, field).trim();
    if (!sameFieldValue(liveValue, baseValue)) out.set(field, liveValue);
  }
  return out;
}

/**
 * f(per-page text runs, extracted values) → synthesized BillFieldRegion[].
 * Pure and synchronous; safe to memoise on (pages, bill) identity.
 */
export function deriveBillFieldRegions(
  pages: TextBox[][],
  values: BillRegionValues,
): BillFieldRegion[] {
  const indexes = pages.map(buildPageIndex);
  const out: BillFieldRegion[] = [];

  const addTextMatch = (field: string, value: string | null | undefined) => {
    const target = value ? tokenize(value) : [];
    if (target.length === 0) return;
    for (let p = 0; p < indexes.length; p++) {
      const m = matchTokens(indexes[p], target);
      if (m) {
        out.push(...regionsFromConsumed(indexes[p], p, field, m.consumed));
        return;
      }
    }
  };

  const addDateMatch = (field: string, value: string | null | undefined) => {
    if (!value) return;
    for (const cand of dateCandidates(value)) {
      for (let p = 0; p < indexes.length; p++) {
        const m = matchTokens(indexes[p], cand);
        if (m && m.matched === Math.min(cand.length, 60)) {
          out.push(...regionsFromConsumed(indexes[p], p, field, m.consumed));
          return;
        }
      }
    }
  };

  const addLabelledAmount = (
    field: string,
    value: string | null | undefined,
    label: 'subtotal' | 'gst' | 'total',
  ) => {
    const norm = normAmount(value);
    if (!norm) return;
    // Search from the LAST page backwards — totals sit at the document end.
    for (let p = indexes.length - 1; p >= 0; p--) {
      const ti = pickAmountToken(indexes[p], norm, label);
      if (ti !== null) {
        out.push(...regionsFromConsumed(indexes[p], p, field, [ti]));
        return;
      }
    }
  };

  // ABN-style digit runs: "94 139 998 244" — match the digit concatenation of
  // consecutive numeric tokens within one row.
  const addDigitsMatch = (field: string, value: string | null | undefined) => {
    if (!value) return;
    const digits = value.replace(/\D/g, '');
    if (digits.length < 4) return;
    for (let p = 0; p < indexes.length; p++) {
      const page = indexes[p];
      for (let i = 0; i < page.tokens.length; i++) {
        if (!/^\d/.test(page.tokens[i].norm)) continue;
        let acc = '';
        const consumed: number[] = [];
        for (
          let j = i;
          j < page.tokens.length && page.tokens[j].row === page.tokens[i].row;
          j++
        ) {
          const tok = page.tokens[j].norm.replace(/\D/g, '');
          if (!tok) break;
          acc += tok;
          consumed.push(j);
          if (acc === digits) {
            out.push(...regionsFromConsumed(page, p, field, consumed));
            return;
          }
          if (acc.length >= digits.length) break;
        }
      }
    }
  };

  addTextMatch('vendor_name', values.vendor_name);
  addTextMatch('vendor_email', values.vendor_email);
  addDigitsMatch('vendor_abn', values.vendor_abn);
  addTextMatch('invoice_number', values.invoice_number);
  addTextMatch('reference', values.reference);
  addDateMatch('invoice_date', values.invoice_date);
  addDateMatch('due_date', values.due_date);
  addLabelledAmount('subtotal', values.subtotal, 'subtotal');
  addLabelledAmount('gst', values.gst, 'gst');
  addLabelledAmount('total', values.total, 'total');

  // Line items: anchor the description first; its row band then disambiguates
  // the numeric cells (a "235.00" or "0.00" repeats across many rows).
  for (let idx = 0; idx < (values.line_items?.length ?? 0); idx++) {
    const line = values.line_items![idx];
    const target = line.description ? tokenize(line.description) : [];
    let band: { page: number; rows: Set<number>; descX1: number } | null = null;
    if (target.length > 0) {
      for (let p = 0; p < indexes.length; p++) {
        const m = matchTokens(indexes[p], target);
        if (!m) continue;
        out.push(
          ...regionsFromConsumed(indexes[p], p, lineFieldKey(idx, 'description'), m.consumed),
        );
        const rows = new Set<number>();
        let descX1 = 0;
        for (const ti of m.consumed) {
          const tok = indexes[p].tokens[ti];
          rows.add(tok.row);
          descX1 = Math.max(descX1, indexes[p].boxes[tok.run].x1);
        }
        band = { page: p, rows, descX1 };
        break;
      }
    }
    if (!band) continue; // no anchor → skip numerics rather than guess wrong

    const page = indexes[band.page];
    // Candidates: numeric tokens on the line's rows, right of the description.
    const inBand = (ti: number) => {
      const tok = page.tokens[ti];
      return band!.rows.has(tok.row) && page.boxes[tok.run].x0 >= band!.descX1 - 0.01;
    };
    const used = new Set<number>();
    const pick = (
      value: number | null | undefined,
      prefer: 'leftmost' | 'rightmost',
    ): number | null => {
      const norm = normAmount(value ?? null);
      if (!norm) return null;
      const cands = tokensEqual(page, norm)
        .filter((ti) => inBand(ti) && !used.has(ti))
        .sort((a, b) => page.boxes[page.tokens[a].run].x0 - page.boxes[page.tokens[b].run].x0);
      if (cands.length === 0) return null;
      const ti = prefer === 'leftmost' ? cands[0] : cands[cands.length - 1];
      used.add(ti);
      return ti;
    };
    // Total first (rightmost column), then qty (leftmost), then unit price —
    // this resolves rows where two cells print the same value (e.g. 0.00).
    const totalTi = pick(line.line_total, 'rightmost');
    const qtyTi = pick(line.quantity, 'leftmost');
    const unitTi = pick(line.unit_amount, 'leftmost');
    if (totalTi !== null) {
      out.push(...regionsFromConsumed(page, band.page, lineFieldKey(idx, 'line_total'), [totalTi]));
    }
    if (qtyTi !== null) {
      out.push(...regionsFromConsumed(page, band.page, lineFieldKey(idx, 'quantity'), [qtyTi]));
    }
    if (unitTi !== null) {
      out.push(...regionsFromConsumed(page, band.page, lineFieldKey(idx, 'unit_amount'), [unitTi]));
    }
  }

  return out;
}
